import { useQuery } from "@apollo/client/react"
import { CircularProgress } from "@mui/material"
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api"
import React from "react"

import { GetAppConfigDocument, type GetAppConfigQuery } from "@/graphql/graphql"
import i18n from "@/i18n"

type VenueGoogleMapProps = {
  latitude: number
  longitude: number
  venueName?: string | null
  zoom?: number
}

const DEFAULT_ZOOM = 15

const containerStyle = { width: "100%", height: "100%" } as const

const MAP_OPTIONS = {
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: "greedy",
  clickableIcons: false,
} as const

const Loading: React.FC = () => (
  <div
    className="flex h-full w-full items-center justify-center bg-gray-100"
    data-testid="public-venue-google-map-loading"
  >
    <CircularProgress size={24} />
  </div>
)

const LoadedMap: React.FC<VenueGoogleMapProps & { apiKey: string }> = ({
  apiKey,
  latitude,
  longitude,
  venueName,
  zoom,
}) => {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: apiKey })

  if (!isLoaded) return <Loading />

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={{ lat: latitude, lng: longitude }}
      zoom={zoom ?? DEFAULT_ZOOM}
      options={MAP_OPTIONS}
    >
      <Marker position={{ lat: latitude, lng: longitude }} title={venueName ?? undefined} />
    </GoogleMap>
  )
}

const VenueGoogleMap: React.FC<VenueGoogleMapProps> = (props) => {
  const { data, loading } = useQuery<GetAppConfigQuery>(GetAppConfigDocument)
  const apiKey = data?.appConfig?.googleMapsApiKey

  if (loading) return <Loading />

  if (!apiKey) {
    return (
      <iframe
        title={props.venueName ?? i18n.t("app.public.venue.venue_info.title")}
        width="100%"
        height="100%"
        style={{ border: 0 }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        src={`https://maps.google.com/maps?q=${props.latitude},${props.longitude}&output=embed&z=${props.zoom ?? DEFAULT_ZOOM}`}
        data-testid="public-venue-google-map-iframe"
      />
    )
  }

  return <LoadedMap apiKey={apiKey} {...props} />
}

export default VenueGoogleMap
